import { inject } from '@angular/core';
import type { MatDialogRef } from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import type { CanActivateFn, GuardResult, UrlTree } from '@angular/router';
import { Router } from '@angular/router';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ConfirmDialogComponent } from '@app/components/dialogs/confirm-dialog/confirm-dialog.component';
import { LogoutReason } from '@app/domain/enums';
import { AuthService } from '@app/services/auth/auth';
import { CountryNotAllowedError } from '@app/services/location/country-not-allowed.error';
import { LocationService } from '@app/services/location/location';

const LOCATION_ERROR_TITLE = 'We couldn\'t verify your location';

const LOCATION_ERROR_DESCRIPTION = 'Match Bingo can only be played in the UK, so we need to confirm your location. '
  + 'Please check your internet connection and try again.';

/** Resolves true for "Try again", false for "Log out". */
function askToRetry$(dialog: MatDialog): Observable<boolean> {
  const dialogRef: MatDialogRef<ConfirmDialogComponent, boolean> = dialog.open(ConfirmDialogComponent, {
    disableClose: true,
    data: {
      title: LOCATION_ERROR_TITLE,
      description: LOCATION_ERROR_DESCRIPTION,
      buttons: [
        {
          label: 'Log out',
          action: (): void => {
            dialogRef.close(false);
          },
        },
        {
          label: 'Try again',
          action: (): void => {
            dialogRef.close(true);
          },
        },
      ],
    },
  });

  return dialogRef.afterClosed().pipe(map(result => result === true));
}

/**
 * Geo/jurisdiction gate for the private area (UK licence requirement). Only a definitive
 * "outside the UK" answer ends the session; a check that could not complete (offline on PWA
 * resume, API outage) keeps the user out of the app but lets them retry without logging out.
 */
export const locationGuard: CanActivateFn = route => {
  const locationService = inject(LocationService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const dialog = inject(MatDialog);

  const leave = (reason: LogoutReason): UrlTree => {
    authService.logout(reason);

    return router.createUrlTree(['/welcome'], { queryParams: route.queryParams });
  };

  const verify$ = (): Observable<GuardResult> => locationService.checkCountry$().pipe(
    map(() => true as const),
    catchError((error: unknown) => {
      if (error instanceof CountryNotAllowedError) {
        return of(leave(LogoutReason.GEO_BLOCKED));
      }

      return askToRetry$(dialog).pipe(
        switchMap((retryCheck): Observable<GuardResult> => {
          if (retryCheck) {
            return verify$();
          }

          return of(leave(LogoutReason.GEO_CHECK_FAILED));
        }),
      );
    }),
  );

  return verify$();
};
